import React, { useState } from "react";
import { API } from "../../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";

const emptyForm = { first_name: "", last_name: "", email: "", phone: "", company: "" };

export function DemoSignupModal({ open, onClose }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { email, message }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim()) e.last_name = "Required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    const digits = form.phone.replace(/[^0-9+]/g, "");
    if (!/^(\+?61|0)4\d{8}$/.test(digits)) e.phone = "Enter a valid AU mobile (04XX XXX XXX)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const allValid =
    form.first_name.trim() &&
    form.last_name.trim() &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) &&
    /^(\+?61|0)4\d{8}$/.test(form.phone.replace(/[^0-9+]/g, ""));

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/demo/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess({ email: data.email, message: data.message });
      } else {
        setErrors({ form: data.detail || "Could not submit request" });
      }
    } catch {
      setErrors({ form: "Network error — please try again" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm(emptyForm);
    setErrors({});
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="demo-signup-modal">
        {success ? (
          <div className="text-center py-4" data-testid="demo-signup-success">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
            <h2 className="text-xl font-bold text-slate-900 mt-3">Request received</h2>
            <p className="text-slate-600 mt-2">{success.message}</p>
            <p className="text-sm text-slate-500 mt-3">
              We've sent a confirmation to{" "}
              <span className="font-semibold text-slate-800">{success.email}</span>
            </p>
            <Button onClick={handleClose} className="mt-5 bg-amber-500 hover:bg-amber-600" data-testid="demo-signup-done-btn">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Start your free 30-day trial</DialogTitle>
              <DialogDescription>
                Full access. One site. No credit card. We'll confirm within 15 minutes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>First name *</Label>
                  <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="demo-first-name" />
                  {errors.first_name && <p className="text-xs text-rose-600 mt-1">{errors.first_name}</p>}
                </div>
                <div>
                  <Label>Surname *</Label>
                  <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="demo-last-name" />
                  {errors.last_name && <p className="text-xs text-rose-600 mt-1">{errors.last_name}</p>}
                </div>
              </div>
              <div>
                <Label>Email address *</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" data-testid="demo-email" />
                {errors.email && <p className="text-xs text-rose-600 mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label>Phone number *</Label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="04XX XXX XXX" data-testid="demo-phone" />
                {errors.phone && <p className="text-xs text-rose-600 mt-1">{errors.phone}</p>}
              </div>
              <div>
                <Label>Company / trading name</Label>
                <Input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Optional" data-testid="demo-company" />
              </div>
              {errors.form && <p className="text-sm text-rose-600">{errors.form}</p>}
              <Button
                onClick={submit}
                disabled={!allValid || submitting}
                className="w-full h-11 bg-amber-500 hover:bg-amber-600 font-semibold"
                data-testid="demo-request-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Free Trial"}
              </Button>
              <p className="text-[11px] leading-snug text-slate-400">
                By requesting a trial you agree to SiteProApp's Terms of Service and Privacy Policy.
                Your details are used only to set up and manage your trial account. We will not share
                your information with third parties.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
